/**
 * PLAnalysisTab — Profit & Loss analysis tab
 *
 * Self-contained:
 *  - Owns its own view/year/month/week/range state.
 *  - Fetches its own P&L data from /api/super-admin/financials/pl-data.
 *  - Renders summary cards, period detail table, P&L bar chart,
 *    YearHeatmap, CashFlowPositionSummary, CashFlowAnalysisCard,
 *    PersonalFinanceCard.
 *
 * Extracted from FinancialsTab.jsx (Feb 2026 refactor — iter138.9al).
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { TrendingUp, TrendingDown, Download } from 'lucide-react';
import { toast } from 'sonner';
import CashFlowAnalysisCard from '../CashFlowAnalysisCard';
import CashFlowPositionSummary from '../CashFlowPositionSummary';
import YearHeatmapCard from '../YearHeatmapCard';
import PersonalFinanceCard from '../PersonalFinanceCard';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 }).format(amount || 0);

const toLocalDateStr = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

export default function PLAnalysisTab() {
  const { isDisabled } = useFeatureFlags();
  const today = new Date();
  const todayLocal = toLocalDateStr(today);

  const [view, setView] = useState('monthly');
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [week, setWeek] = useState(1);
  const [rangeStart, setRangeStart] = useState(toLocalDateStr(new Date(today.getFullYear(), today.getMonth(), 1)));
  const [rangeEnd, setRangeEnd] = useState(todayLocal);
  const [exporting, setExporting] = useState(false);
  const [plData, setPLData] = useState([]);
  const [plLoading, setPLLoading] = useState(true);

  const fetchPLData = useCallback(async () => {
    setPLLoading(true);
    try {
      const params = new URLSearchParams({ view });
      if (view === 'range') {
        if (!rangeStart || !rangeEnd) { setPLLoading(false); return; }
        params.append('start_date', rangeStart);
        params.append('end_date', rangeEnd);
      } else {
        params.append('year', year.toString());
        if (view !== 'yearly') params.append('month', month.toString());
        if (view === 'weekly') params.append('week', week.toString());
      }
      const response = await fetch(`${BACKEND_URL}/api/super-admin/financials/pl-data?${params}`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setPLData(data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch P&L data:', error);
    } finally {
      setPLLoading(false);
    }
  }, [view, year, month, week, rangeStart, rangeEnd]);

  useEffect(() => {
    fetchPLData();
  }, [fetchPLData]);

  const periodTotals = useMemo(() => plData.reduce((acc, item) => ({
    system_turnover: acc.system_turnover + (item.system_turnover || 0),
    banking_total: acc.banking_total + (item.banking_total || 0),
    workshop_expense: acc.workshop_expense + (item.expense_total || 0),
    fixed_expense: acc.fixed_expense + (item.fixed_expense_total || 0),
    total_expense: acc.total_expense + (item.total_expense || 0),
    net: acc.net + (item.net_profit_loss || 0),
  }), { system_turnover: 0, banking_total: 0, workshop_expense: 0, fixed_expense: 0, total_expense: 0, net: 0 }), [plData]);

  const handleExportPL = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams({ view: view === 'yearly' ? 'monthly' : view });
      if (view === 'range') {
        params.set('start_date', rangeStart);
        params.set('end_date', rangeEnd);
      } else {
        params.set('year', year.toString());
        params.set('month', month.toString());
        if (view === 'weekly') params.set('week', week.toString());
      }
      const response = await fetch(`${BACKEND_URL}/api/super-admin/financials/pl-export?${params}`, { credentials: 'include' });
      if (!response.ok) {
        toast.error('Failed to export P&L report');
        return;
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const filename = view === 'range'
        ? `PnL_${rangeStart}_to_${rangeEnd}.xlsx`
        : view === 'weekly'
          ? `PnL_Week${week}_${MONTH_NAMES[month - 1]}_${year}.xlsx`
          : `PnL_${MONTH_NAMES[month - 1]}_${year}.xlsx`;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('P&L report exported');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export P&L report');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="pl-analysis-tab">
      {/* P&L Chart Section */}
      <Card className="p-6">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Profit & Loss Analysis</h3>
            <p className="text-sm text-slate-500">
              {view === 'yearly' ? `Year ${year}` :
                view === 'monthly' ? `${MONTH_NAMES[month - 1]} ${year}` :
                  view === 'weekly' ? `Week ${week}, ${MONTH_NAMES[month - 1]} ${year}` :
                    `${rangeStart} to ${rangeEnd}`}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Select value={view} onValueChange={setView}>
              <SelectTrigger className="w-[130px]" data-testid="pl-view-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="yearly">Yearly</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
                <SelectItem value="weekly">Weekly</SelectItem>
                <SelectItem value="range">Date Range</SelectItem>
              </SelectContent>
            </Select>

            {view !== 'range' && (
              <Select value={year.toString()} onValueChange={(v) => setYear(parseInt(v))}>
                <SelectTrigger className="w-[100px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[2024, 2025, 2026, 2027].map(y => (<SelectItem key={y} value={y.toString()}>{y}</SelectItem>))}
                </SelectContent>
              </Select>
            )}

            {view !== 'yearly' && view !== 'range' && (
              <Select value={month.toString()} onValueChange={(v) => setMonth(parseInt(v))}>
                <SelectTrigger className="w-[130px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MONTH_NAMES.map((m, i) => (<SelectItem key={i} value={(i + 1).toString()}>{m}</SelectItem>))}
                </SelectContent>
              </Select>
            )}

            {view === 'weekly' && (
              <Select value={week.toString()} onValueChange={(v) => setWeek(parseInt(v))}>
                <SelectTrigger className="w-[100px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 4, 5].map(w => (<SelectItem key={w} value={w.toString()}>Week {w}</SelectItem>))}
                </SelectContent>
              </Select>
            )}

            {view === 'range' && (
              <>
                <Input type="date" value={rangeStart} onChange={(e) => setRangeStart(e.target.value)} className="w-[160px]" data-testid="pl-range-start" />
                <span className="text-slate-400">to</span>
                <Input type="date" value={rangeEnd} onChange={(e) => setRangeEnd(e.target.value)} className="w-[160px]" data-testid="pl-range-end" />
              </>
            )}

            <Button
              variant="outline"
              onClick={handleExportPL}
              disabled={exporting || (view === 'range' && (!rangeStart || !rangeEnd))}
              data-testid="pl-export-btn"
            >
              <Download className="h-4 w-4 mr-2" />
              {exporting ? 'Exporting...' : 'Export Excel'}
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          <div className="p-3 bg-blue-50 rounded-lg border border-blue-200" data-testid="summary-system-turnover">
            <p className="text-xs text-blue-600">Total Turnover (System)</p>
            <p className="text-lg font-bold text-blue-900">{formatCurrency(periodTotals.system_turnover)}</p>
            <p className="text-[10px] text-blue-500 mt-1">From invoices</p>
          </div>
          <div className="p-3 bg-green-50 rounded-lg border border-green-200" data-testid="summary-banking">
            <p className="text-xs text-green-600">Total Income</p>
            <p className="text-lg font-bold text-green-900">{formatCurrency(periodTotals.banking_total)}</p>
            <p className="text-[10px] text-green-500 mt-1">Banking + Cash + Transfer</p>
          </div>
          <div className="p-3 bg-orange-50 rounded-lg border border-orange-200" data-testid="summary-fixed-expense">
            <p className="text-xs text-orange-600">Fixed Expense</p>
            <p className="text-lg font-bold text-orange-900">{formatCurrency(periodTotals.fixed_expense)}</p>
            <p className="text-[10px] text-orange-500 mt-1">Wages, rent, etc.</p>
          </div>
          <div className="p-3 bg-red-50 rounded-lg border border-red-200" data-testid="summary-total-expense">
            <p className="text-xs text-red-600">Total Expense</p>
            <p className="text-lg font-bold text-red-900">{formatCurrency(periodTotals.total_expense)}</p>
            <p className="text-[10px] text-red-500 mt-1">Workshop + Fixed</p>
          </div>
          <div className={`p-3 rounded-lg border ${periodTotals.net >= 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-rose-50 border-rose-200'}`} data-testid="summary-net">
            <p className={`text-xs ${periodTotals.net >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>Net P&L</p>
            <div className="flex items-center gap-1">
              {periodTotals.net >= 0 ? <TrendingUp className="h-4 w-4 text-emerald-600" /> : <TrendingDown className="h-4 w-4 text-rose-600" />}
              <p className={`text-lg font-bold ${periodTotals.net >= 0 ? 'text-emerald-900' : 'text-rose-900'}`}>
                {formatCurrency(Math.abs(periodTotals.net))}
              </p>
            </div>
            <p className="text-[10px] text-slate-500 mt-1">Total Income − Total Expense</p>
          </div>
        </div>

        {/* Per-period detail table */}
        <div className="overflow-x-auto rounded-lg border border-slate-200 mb-4" data-testid="pl-detail-table">
          <table className="min-w-full text-xs">
            <thead className="bg-slate-100 text-slate-600 uppercase text-[10px] tracking-wider">
              <tr>
                <th className="px-3 py-2 text-left font-bold sticky left-0 bg-slate-100">Period</th>
                <th className="px-3 py-2 text-right">Turnover</th>
                <th className="px-3 py-2 text-right text-green-700">Income</th>
                <th className="px-3 py-2 text-right text-green-600">Cash</th>
                <th className="px-3 py-2 text-right text-green-600">Transfer</th>
                <th className="px-3 py-2 text-right text-green-800 font-extrabold">Total Income</th>
                <th className="px-3 py-2 text-right text-rose-700">Workshop Exp.</th>
                <th className="px-3 py-2 text-right text-orange-700">Fixed Exp.</th>
                <th className="px-3 py-2 text-right text-red-800 font-extrabold">Total Exp.</th>
                <th className="px-3 py-2 text-right font-extrabold">Net Profit</th>
              </tr>
            </thead>
            <tbody>
              {plData.length === 0 ? (
                <tr><td colSpan={10} className="px-3 py-6 text-center text-slate-400 italic">No P&L data for this period yet.</td></tr>
              ) : plData.map((row, idx) => {
                const totalInc = row.banking_total || 0;
                const workshop = row.workshop_expense ?? row.expense_total ?? 0;
                const fixedExp = row.fixed_expense ?? row.fixed_expense_total ?? 0;
                const totalExp = row.total_expense || (workshop + fixedExp);
                const net = row.net_profit_loss || 0;
                return (
                  <tr key={idx} className="border-t hover:bg-slate-50" data-testid={`pl-row-${row.period}`}>
                    <td className="px-3 py-1.5 font-semibold sticky left-0 bg-white">{row.period}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-blue-700">{formatCurrency(row.system_turnover || 0)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{formatCurrency(row.banking || 0)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{formatCurrency(row.income_cash || 0)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{formatCurrency(row.transfer || 0)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums font-bold text-green-800">{formatCurrency(totalInc)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-rose-700">{formatCurrency(workshop)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-orange-700">{formatCurrency(fixedExp)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums font-bold text-red-800">{formatCurrency(totalExp)}</td>
                    <td className={`px-3 py-1.5 text-right tabular-nums font-extrabold ${net >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                      {net >= 0 ? '' : '−'}{formatCurrency(Math.abs(net))}
                    </td>
                  </tr>
                );
              })}
              {plData.length > 0 && (
                <tr className="border-t-2 border-slate-400 bg-slate-50 font-bold">
                  <td className="px-3 py-2 sticky left-0 bg-slate-50 uppercase tracking-wider text-[10px]">Totals</td>
                  <td className="px-3 py-2 text-right tabular-nums text-blue-700">{formatCurrency(periodTotals.system_turnover)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{formatCurrency(plData.reduce((a, r) => a + (r.banking || 0), 0))}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{formatCurrency(plData.reduce((a, r) => a + (r.income_cash || 0), 0))}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{formatCurrency(plData.reduce((a, r) => a + (r.transfer || 0), 0))}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-green-800">{formatCurrency(periodTotals.banking_total)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-rose-700">{formatCurrency(plData.reduce((a, r) => a + (r.workshop_expense ?? r.expense_total ?? 0), 0))}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-orange-700">{formatCurrency(periodTotals.fixed_expense)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-red-800">{formatCurrency(periodTotals.total_expense)}</td>
                  <td className={`px-3 py-2 text-right tabular-nums ${periodTotals.net >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                    {periodTotals.net >= 0 ? '' : '−'}{formatCurrency(Math.abs(periodTotals.net))}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* P&L Bar Chart */}
        {plLoading ? (
          <div className="h-[300px] flex items-center justify-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-blue-600 border-t-transparent"></div>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={plData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="period" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(value, name) => [formatCurrency(value), name]} labelFormatter={(label) => `Period: ${label}`} />
              <Legend />
              <Bar dataKey="system_turnover" name="Turnover (System)" fill="#3b82f6" radius={[4, 4, 0, 0]} />
              <Bar dataKey="banking_total" name="Total Income" fill="#22c55e" radius={[4, 4, 0, 0]} />
              <Bar dataKey="total_expense" name="Total Expense" fill="#ef4444" radius={[4, 4, 0, 0]} />
              <Bar dataKey="net_profit_loss" name="Net P&L" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Card>

      {/* Year-at-a-glance Heat-map */}
      <YearHeatmapCard BACKEND_URL={BACKEND_URL} />

      {/* Company + Personal Cash Flow Position */}
      {!isDisabled('financials_cash_flow_position') && (
        <CashFlowPositionSummary
          BACKEND_URL={BACKEND_URL}
          view={view}
          year={year}
          month={month}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
        />
      )}

      {/* Cash Flow / Solvency Analysis */}
      <CashFlowAnalysisCard
        BACKEND_URL={BACKEND_URL}
        view={view}
        year={year}
        month={month}
        rangeStart={rangeStart}
        rangeEnd={rangeEnd}
      />

      {/* Personal Finance — private to super admin owner */}
      {!isDisabled('financials_personal_finance') && <PersonalFinanceCard BACKEND_URL={BACKEND_URL} />}
    </div>
  );
}
